import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowLeft,
  Wifi,
  ShoppingCart,
  Loader,
  AlertCircle,
  Phone,
  X,
} from "lucide-react";
import { productsService } from "../services/productsService";
// import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { formatMoney, parseMoney } from "../utils/money";

// Skeleton Loader Component
const ProductSkeleton = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3 flex-1">
        <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
        <div className="min-w-0 flex-1">
          <div className="h-5 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-16"></div>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-3">
        <div className="text-right">
          <div className="h-5 bg-gray-200 rounded w-12"></div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-12"></div>
      </div>
    </div>
  </div>
);

const Products = ({ category, onBack, walletBalances, onBalanceUpdate }) => {
  // const { user } = useAuth();
  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [modalError, setModalError] = useState(""); // Separate error state for modal
  const [selectedNetwork, setSelectedNetwork] = useState("all");
  const [purchasing, setPurchasing] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [recipientPhone, setRecipientPhone] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 7;

  // Refs for infinite scroll
  const observerRef = useRef();
  const lastProductElementRef = useRef();

  const networks = [
    { value: "all", label: "All", icon: null },
    { value: "mtn", label: "MTN", icon: "/mtn.jpg" },
    { value: "telecel", label: "Telecel", icon: "/telecel.jpg" },
    { value: "airteltigo", label: "AirtelTigo", icon: "/at.jpg" },
  ];

  const currentCategory = {
    "affordable-data": {
      title: "Affordable Data",
      subtitle: "Discounted data packages",
      walletType: "affordable_data",
    },
    "airtime-data": {
      title: "Airtime & Data",
      subtitle: "Business data packages",
      walletType: "airtime_data",
    },
  }[category];

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await productsService.getAffordableDataProducts(
          selectedNetwork === "all" ? null : selectedNetwork,
        );
        if (response.status === "success") {
          const products = response.data.products || [];
          setAllProducts(products);
        } else {
          setError("Failed to load products");
        }
      } catch (err) {
        setError("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category, selectedNetwork]);

  // Filter products by network and reset pagination
  useEffect(() => {
    if (selectedNetwork === "all") {
      setFilteredProducts(allProducts);
    } else {
      setFilteredProducts(
        allProducts.filter((p) => p.network === selectedNetwork),
      );
    }

    // Reset pagination when filter changes
    setCurrentPage(1);
    setHasMore(true);
  }, [allProducts, selectedNetwork]);

  // Update displayed products when filtered products or page changes
  useEffect(() => {
    const startIndex = 0;
    const endIndex = currentPage * ITEMS_PER_PAGE;
    const newDisplayedProducts = filteredProducts.slice(startIndex, endIndex);

    setDisplayedProducts(newDisplayedProducts);
    setHasMore(endIndex < filteredProducts.length);
  }, [filteredProducts, currentPage]);

  // Infinite scroll callback
  const lastProductElementCallback = useCallback(
    (node) => {
      if (loading || loadingMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setLoadingMore(true);

          // Simulate network delay for smooth UX
          setTimeout(() => {
            setCurrentPage((prevPage) => prevPage + 1);
            setLoadingMore(false);
          }, 800);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, loadingMore, hasMore],
  );

  // Assign ref to last element
  useEffect(() => {
    if (lastProductElementRef.current) {
      lastProductElementCallback(lastProductElementRef.current);
    }
  }, [displayedProducts, lastProductElementCallback]);

  const handlePurchaseClick = (product, e) => {
    // Prevent event bubbling if Buy button is clicked
    if (e && e.target.closest(".buy-button")) {
      return;
    }

    setSelectedProduct(product);
    setRecipientPhone("");
    setModalError(""); // Clear modal errors when opening
  };

  const handleBuyButtonClick = (product, e) => {
    e.stopPropagation(); // Prevent card click
    handlePurchaseClick(product);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
    setRecipientPhone("");
    setModalError(""); // Clear modal errors when closing
  };

  const handlePurchaseConfirm = async () => {
    setModalError(""); // Clear previous errors

    // Clean phone number (remove spaces, dashes, etc.)
    const cleanedPhone = recipientPhone.replace(/\D/g, "");

    // Validation checks
    if (!recipientPhone) {
      setModalError("Please enter a phone number");
      return;
    }

    if (!/^[0-9\s\-()]+$/.test(recipientPhone)) {
      setModalError("Phone number can only contain numbers");
      return;
    }

    if (cleanedPhone.length < 10) {
      setModalError("Phone number must be at least 10 digits");
      return;
    }

    setPurchasing(selectedProduct.id);

    try {
      const walletBalance = parseMoney(
        walletBalances?.[currentCategory.walletType + "_balance"],
      );
      const productPrice = parseMoney(selectedProduct.price);

      if (walletBalance < productPrice) {
        setModalError(
          `Insufficient balance. Need ₵${formatMoney(productPrice)}, have ₵${formatMoney(walletBalance)}`,
        );
        setPurchasing(null);
        return;
      }

      const purchaseData = {
        product_id: selectedProduct.id,
        wallet_type: currentCategory.walletType,
        recipient_phone: cleanedPhone, // Send cleaned phone number
      };

      const response = await productsService.purchaseProduct(purchaseData);

      if (response.status === "success") {
        toast.success(
          `${selectedProduct.data_amount} sent to ${recipientPhone}!`,
        );
        setSelectedProduct(null);
        setRecipientPhone("");
        setModalError("");

        if (onBalanceUpdate) {
          onBalanceUpdate();
        }
      } else {
        setModalError(response.message || "Purchase failed");
      }
    } catch (err) {
      setModalError(err.response?.data?.message || "Purchase failed");
    } finally {
      setPurchasing(null);
    }
  };

  const getNetworkIcon = (networkValue) => {
    const network = networks.find((n) => n.value === networkValue);
    return network?.icon;
  };

  // Check if phone number is valid for button disable state
  const cleanedPhone = recipientPhone.replace(/\D/g, "");
  const isPhoneValid =
    recipientPhone &&
    /^[0-9\s\-()]+$/.test(recipientPhone) &&
    cleanedPhone.length >= 10;

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-gray-200 rounded-lg animate-pulse"></div>
            <div>
              <div className="h-6 bg-gray-200 rounded w-32 animate-pulse mb-1"></div>
              <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
            </div>
          </div>
          <div className="text-right">
            <div className="h-4 bg-gray-200 rounded w-12 animate-pulse mb-1"></div>
            <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
          </div>
        </div>

        {/* Network Filter Skeleton */}
        <div className="flex space-x-2">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-9 bg-gray-200 rounded-lg w-20 animate-pulse"
            ></div>
          ))}
        </div>

        {/* Products Skeleton */}
        <div className="space-y-3">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
              {currentCategory?.title}
            </h1>
            <p className="text-sm text-gray-500">{currentCategory?.subtitle}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs sm:text-sm text-gray-500">Balance</p>
          <p className="text-sm sm:text-lg font-semibold text-gray-900">
            ₵{formatMoney(walletBalances?.[currentCategory.walletType + "_balance"])}
          </p>
        </div>
      </div>

      {/* Network Filter with Icons */}
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {networks.map((network) => (
          <button
            key={network.value}
            onClick={() => setSelectedNetwork(network.value)}
            className={`flex-shrink-0 flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
              selectedNetwork === network.value
                ? "border-gray-900 bg-gray-900 text-white"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {network.icon && (
              <img
                src={network.icon}
                alt={network.label}
                className="w-4 h-4 object-contain rounded"
              />
            )}
            <span className="text-sm font-medium">{network.label}</span>
          </button>
        ))}
      </div>

      {/* Error (only for general component errors, not modal errors) */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Product Cards with Infinite Scroll */}
      {displayedProducts.length > 0 ? (
        <div className="space-y-3">
          {displayedProducts.map((product, index) => {
            const networkIcon = getNetworkIcon(product.network);
            const isLast = index === displayedProducts.length - 1;

            return (
              <div
                key={product.id}
                ref={isLast ? lastProductElementRef : null}
                onClick={(e) => handlePurchaseClick(product, e)}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer group"
              >
                <div className="flex items-center justify-between">
                  {/* Left: Product Info */}
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center group-hover:bg-gray-100 transition-colors">
                      <Wifi className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-900 group-hover:text-gray-700 transition-colors">
                        {product.data_amount}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        {networkIcon && (
                          <img
                            src={networkIcon}
                            alt={product.network}
                            className="w-4 h-4 object-contain rounded"
                          />
                        )}
                        <span className="text-sm text-gray-500 uppercase font-medium">
                          {product.network}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Price & Button */}
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className="font-bold text-gray-900">
                        ₵{formatMoney(product.price)}
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleBuyButtonClick(product, e)}
                      className="buy-button bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium py-2 px-3 sm:px-4 rounded-lg transition-colors"
                    >
                      Buy
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Loading More Skeleton */}
          {loadingMore && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <ProductSkeleton key={`loading-${i}`} />
              ))}
            </div>
          )}

          {/* End of Results */}
          {!hasMore && displayedProducts.length > 0 && (
            <div className="text-center py-8">
              <div className="text-gray-500 text-sm">
                You've reached the end of the list
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12">
          <Wifi className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-1">No packages found</h3>
          <p className="text-gray-500">Try selecting a different network</p>
        </div>
      )}

      {/* Purchase Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Purchase Data
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Product Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getNetworkIcon(selectedProduct.network) && (
                      <img
                        src={getNetworkIcon(selectedProduct.network)}
                        alt={selectedProduct.network}
                        className="w-5 h-5 object-contain rounded"
                      />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">
                        {selectedProduct.data_amount}
                      </div>
                      <div className="text-sm text-gray-500 uppercase">
                        {selectedProduct.network}
                      </div>
                    </div>
                  </div>
                  <div className="font-bold text-gray-900">
                    ₵{formatMoney(selectedProduct.price)}
                  </div>
                </div>
              </div>

              {/* Recipient Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={recipientPhone}
                    onChange={(e) => setRecipientPhone(e.target.value)}
                    placeholder="024 123 4567"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              {/* Modal Error */}
              {modalError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <p className="text-red-700 text-sm">{modalError}</p>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePurchaseConfirm}
                  disabled={purchasing === selectedProduct.id || !isPhoneValid}
                  className="flex-1 bg-gray-900 hover:bg-gray-800 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {purchasing === selectedProduct.id ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Buying...</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-4 h-4" />
                      <span>Purchase</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
